import React, { useEffect, useRef } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Button from "components/atoms/button/Button";
import Text from "components/atoms/text/Text";

import Constants from "utils/constants";

const Container = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100vw;
    height: 100vh;
    z-index: 10000;
`;

const Wrapper = styled.div`
    position: relative;
    top: 0;
`;

const AlertContainer = styled.div`
    width: 320px;
    height: auto;
    border: solid 1px var(--color-Outline);
    border-radius: 8px;
    overflow: hidden;
    z-index: 1;
`;

const ContentsSection = styled.div`
    display: flex;
    flex-direction: column;
    padding: 8px;
    width: 100%;
    height: auto;
    background-color: var(--color-Base1);
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    margin: 0 0 12px 0;
`;

const TitleText = styled(Text).attrs({
    variant: "styled-lg",
    fontWeight: "500",
})``;

const IconBox = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 8px 0 0;
    width: 24px;
    height: 24px;
    background-color: ${(props) => props.bgColor};
    border-radius: 4px;
`;

const MessageWrap = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 40px;
    white-space: pre-wrap;
`;

const MessageText = styled(Text).attrs({
    variant: "styled-md",
})``;

const ButtonsSection = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 8px;
    width: 100%;
    height: auto;
    background-color: var(--color-White);
`;

const DimLayer = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    z-index: 1000;

    ${Constants.BLUR_OPTION}

    &:focus {
        outline: none;
    }
`;

function AbstractAlert(props) {
    const { type, title, message, buttonName, onConfirm, onClose } = props;
    const dimRef = useRef(null);
    const getBackgroundColor = () => {
        return type === "info" ? "var(--color-Button1)" : "var(--color-Warn)";
    };

    const getHoverColor = () => {
        return type === "info" ? "var(--color-ButtonHover1)" : "var(--color-WarnHover)";
    };

    const getIconComponent = () => {
        return <Icon name={type === "info" ? "info" : "warn"} size={"12"} color={"var(--color-White)"} />;
    };

    const renderButtons = () => {
        if (!onConfirm) {
            return (
                <Button
                    onlyText
                    width={"64px"}
                    height={"32px"}
                    bgColor={"var(--color-Button1)"}
                    hoverBgColor={"var(--color-ButtonHover1)"}
                    fontColor={"var(--color-White)"}
                    buttonText={"확인"}
                    onClick={onClose}
                    singleButton
                />
            );
        } else {
            return (
                <>
                    <Button
                        onlyText
                        width={"64px"}
                        height={"32px"}
                        bgColor={"var(--color-Button4)"}
                        hoverBgColor={"var(--color-ButtonHover4)"}
                        fontColor={"var(--color-SubBlack)"}
                        buttonText={"취소"}
                        onClick={onClose}
                    />
                    <Button
                        onlyText
                        margin={"0 0 0 4px"}
                        width={"64px"}
                        height={"32px"}
                        bgColor={getBackgroundColor}
                        hoverBgColor={getHoverColor}
                        fontColor={"var(--color-White)"}
                        buttonText={buttonName}
                        onClick={(e) => {
                            onConfirm();
                            onClose();
                        }}
                    />
                </>
            );
        }
    };

    useEffect(() => {
        if (dimRef.current) {
            dimRef.current.focus();
        }
    }, []);

    return (
        <>
            <DimLayer ref={dimRef} tabIndex={-1} />
            <Container>
                <Wrapper className="alert">
                    <AlertContainer>
                        <ContentsSection>
                            <Row>
                                <IconBox bgColor={getBackgroundColor}>{getIconComponent()}</IconBox>
                                <TitleText>{title}</TitleText>
                            </Row>
                            <MessageWrap>
                                <MessageText>{message}</MessageText>
                            </MessageWrap>
                        </ContentsSection>
                        <ButtonsSection>{renderButtons()}</ButtonsSection>
                    </AlertContainer>
                </Wrapper>
            </Container>
        </>
    );
}

export default AbstractAlert;
